import { Team } from "@prisma/client";
import Avatar from "../lib/Avatar";
import { useRouter } from "next/router";
import { FunctionComponent, useEffect, useState } from "react";
import { useUser } from "../../hooks/useUser";
import {
  resetPasswordWithId,
  sendResetPasswordEmail,
} from "../../lib/auth-fetcher";
import Button from "../lib/Button";
import TextField from "../lib/TextField/TextField";
import { hostSlug, reservedAppSlugs } from "@xtribe/shared/lib/host";
import { cache } from "swr";
import { useRedirect } from "@xtribe/shared/hooks/useRedirect";
import Image from "next/image";

interface ForgotPasswordFormProps {
  setPassword?: boolean;
  team?: Team;
  queryString: string | null;
}

const ForgotPasswordForm: FunctionComponent<ForgotPasswordFormProps> = ({
  setPassword,
  team,
  queryString,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [successMsg, setSuccessMsg] = useState<string>("");
  const { user, loading, loggedOut, error, revalidate } = useUser();
  const router = useRouter();
  const verificationId = router.query.verificationId?.toString() || "";
  const { pushToNextPath, getNextPath } = useRedirect({
    defaultPath: "/member",
  });
  useEffect(() => {
    if (user?.id) {
      const slug = hostSlug(window);
      cache.clear();
      let nextPath =
        slug && !reservedAppSlugs.includes(slug)
          ? getNextPath()
            ? getNextPath()
            : "/member"
          : process.env.NEXT_PUBLIC_APP_URL + "/user";
      pushToNextPath(nextPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  async function handleSubmit(event: any) {
    event.preventDefault();
    if (isLoading || loading) return;
    setIsLoading(true);
    const emailElement = event.currentTarget.elements.email;
    const passwordElement = event.currentTarget.elements.password;

    try {
      let res: any;
      if (!setPassword) {
        res = await sendResetPasswordEmail(
          emailElement.value?.trim(),
          team?.avatar ?? "",
          team?.name ?? ""
        );
        setErrorMsg(undefined);
        setSuccessMsg(
          "Wir haben dir eine Email mit dem Link zum Zurücksetzen deines Passworts geschickt."
        );
      } else {
        res = await resetPasswordWithId(
          passwordElement.value?.trim(),
          verificationId,
          team?.avatar ?? "",
          team?.name ?? ""
        );
        setErrorMsg(undefined);
        setSuccessMsg("Dein Passwort wurde erfolgreich geändert.");
        if (res) {
          await revalidate();
        }
      }

      setIsLoading(false);
    } catch (error) {
      setErrorMsg(error.message);
      setSuccessMsg("");
      setIsLoading(false);
    }
  }

  return (
    <div className="max-w-md w-full mx-auto mt-8 sm:mt-32">
      <div className="flex flex-col justify-center items-center text-center">
        {team ? (
          <Avatar
            storageLink={!!team}
            src={team?.avatar ?? "no-image.svg"}
            size="large"
          />
        ) : (
          <Image
            src="/static/logo/xtribe-logo-128.png"
            height={60}
            width={205.78125}
          />
        )}
        <h1 className="normal-case tracking-tight mb-8 mt-4 h4">
          {team ? (
            <>
              Neues Passwort <span className="font-semibold">{team.name}</span>
            </>
          ) : (
            "Neues Passwort für xTribe"
          )}
        </h1>
      </div>
      <form onSubmit={handleSubmit}>
        {errorMsg && <p className="text-red-500 mb-4">{errorMsg}</p>}
        {!!successMsg && <p className="text-green-500 mb-4">{successMsg}</p>}
        {setPassword && (
          <div className="mb-4 sm:mb-6">
            <TextField
              name="password"
              type="password"
              autoComplete="password"
              required
              label="Passwort"
              placeholder="passwort123"
              showLabel={true}
            />
          </div>
        )}
        {!setPassword && (
          <div className="mb-4 sm:mb-6">
            <TextField
              name="email"
              type="email"
              required
              label="E-Mail"
              placeholder="mail@domain.com"
              showLabel={true}
            />
          </div>
        )}
        <Button
          auto
          type="success"
          htmlType="submit"
          loading={isLoading || loading}
        >
          {setPassword ? "Passwort Ändern" : "Passwort Anfordern"}
        </Button>
      </form>
      <div className="mt-6">
        <a
          href={`${process.env.NEXT_PUBLIC_AUTH_URL}/signin${
            queryString ? "?" + queryString : ""
          }`}
          rel="prefetch"
          className="text-right text-sm no-underline hover:underline"
        >
          Anmelden
        </a>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
